import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Bell, DoorOpen, Loader2, RefreshCw, Link2, Check } from 'lucide-react';
import { AdminGate, adminFetch } from '../components/admin/AdminGate';

export const PROGRAMS = [
  { id: 'foundations', label: 'Foundations' },
  { id: 'first_splash', label: 'First Splash' },
  { id: 'group', label: 'Group Membership' },
  { id: 'private', label: 'Private / Semi-Private' },
];
const STATUSES = ['waiting', 'next_in_line', 'spot_open', 'enrolled', 'released', 'removed'];
const STATUS_STYLE = {
  waiting: 'bg-splash-sand/50 text-splash-green', next_in_line: 'bg-splash-mustard/30 text-splash-green',
  spot_open: 'bg-splash-coral/20 text-splash-coralDark', enrolled: 'bg-splash-teal/20 text-splash-tealDark',
  released: 'bg-gray-100 text-gray-500', removed: 'bg-gray-100 text-gray-500',
};
const label = (s) => s.replace(/_/g, ' ');
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—');

function Row({ e, reload }) {
  const [busy, setBusy] = useState('');
  const [notes, setNotes] = useState(e.admin_notes || '');
  const [copied, setCopied] = useState(false);
  const trackerUrl = e.token ? `${window.location.origin}/waitlist/status/${e.token}` : '';
  const copyTracker = async () => {
    await navigator.clipboard.writeText(trackerUrl).catch(() => window.prompt('Copy this tracker link:', trackerUrl));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const patch = async (body) => { setBusy('save'); await adminFetch(`/admin/program-waitlist/${e.id}`, { method: 'PATCH', body: JSON.stringify(body) }); setBusy(''); reload(); };
  const notify = async (kind) => {
    if (kind === 'spot_open' && !window.confirm(`Email ${e.parent_name} that a spot is open? They'll have 7 days to enroll.`)) return;
    setBusy(kind); await adminFetch(`/admin/program-waitlist/${e.id}/notify`, { method: 'POST', body: JSON.stringify({ kind }) }); setBusy(''); reload();
  };
  return (
    <tr className="border-t border-splash-sand/60 align-top" data-testid={`waitlist-row-${e.id}`}>
      <td className="py-3 pr-3 font-display font-bold text-lg text-splash-green">{e.position ? `#${e.position}` : '—'}</td>
      <td className="py-3 pr-3 text-sm">
        <div className="font-semibold text-splash-green">{e.parent_name}</div>
        <div className="text-splash-green/70">{e.email}</div>
        <div className="text-splash-green/70">{e.phone}</div>
      </td>
      <td className="py-3 pr-3 text-sm text-splash-green/85">{e.child_name || '—'}<div className="text-splash-green/60">{e.child_age}</div></td>
      <td className="py-3 pr-3 text-sm">
        <select value={e.status} onChange={(ev) => patch({ status: ev.target.value })} data-testid={`waitlist-status-${e.id}`} className={`rounded-full px-3 py-1 text-xs font-semibold capitalize border-0 ${STATUS_STYLE[e.status]}`}>
          {STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
        </select>
        {e.status === 'spot_open' && <div className="text-xs text-splash-coralDark mt-1">Expires {fmt(e.offer_expires_at)}</div>}
        <div className="text-xs text-splash-green/50 mt-1">Joined {fmt(e.created_at)}</div>
      </td>
      <td className="py-3 pr-3">
        <textarea value={notes} onChange={(ev) => setNotes(ev.target.value)} onBlur={() => notes !== (e.admin_notes || '') && patch({ admin_notes: notes })} rows={2} placeholder="Notes" data-testid={`waitlist-notes-${e.id}`} className="w-40 rounded-xl border border-splash-sand px-2 py-1 text-xs" />
      </td>
      <td className="py-3 text-xs space-y-1.5">
        <button type="button" disabled={!!busy} onClick={() => notify('next_in_line')} data-testid={`waitlist-notify-next-${e.id}`} className="flex items-center gap-1 rounded-full border border-splash-sand px-3 py-1.5 font-semibold text-splash-green whitespace-nowrap">
          {busy === 'next_in_line' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />} Notify next in line
        </button>
        <button type="button" disabled={!!busy} onClick={() => notify('spot_open')} data-testid={`waitlist-notify-open-${e.id}`} className="flex items-center gap-1 rounded-full bg-splash-coral text-white px-3 py-1.5 font-semibold whitespace-nowrap">
          {busy === 'spot_open' ? <Loader2 className="w-3 h-3 animate-spin" /> : <DoorOpen className="w-3 h-3" />} Spot open (7 days)
        </button>
        {trackerUrl && (
          <div className="flex items-center gap-2">
            <button type="button" onClick={copyTracker} data-testid={`waitlist-copy-tracker-${e.id}`} className="flex items-center gap-1 text-splash-teal font-semibold whitespace-nowrap">
              {copied ? <Check className="w-3 h-3" /> : <Link2 className="w-3 h-3" />} {copied ? 'Copied!' : 'Copy tracker link'}
            </button>
            <a href={trackerUrl} target="_blank" rel="noreferrer" data-testid={`waitlist-open-tracker-${e.id}`} className="text-splash-green font-semibold underline">Open</a>
          </div>
        )}
      </td>
    </tr>
  );
}

function Manager() {
  const { program = 'foundations' } = useParams();
  const [entries, setEntries] = useState(null);
  const [showClosed, setShowClosed] = useState(false);
  const load = useCallback(() => {
    adminFetch(`/admin/program-waitlist?program=${program}`).then((r) => r.json()).then((d) => setEntries(d.entries || []));
  }, [program]);
  useEffect(() => { setEntries(null); load(); }, [load]);
  const visible = (entries || []).filter((e) => showClosed || e.position);

  return (
    <section className="rounded-3xl bg-white border border-splash-sand p-5" data-testid="admin-waitlist-manager">
      <div className="flex flex-wrap gap-2 mb-4">
        {PROGRAMS.map((p) => (
          <Link key={p.id} to={`/admin/waitlists/${p.id}`} data-testid={`waitlist-tab-${p.id}`} className={`rounded-full px-4 py-1.5 text-sm font-semibold ${p.id === program ? 'bg-splash-coral text-white' : 'bg-splash-cream text-splash-green'}`}>{p.label}</Link>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h1 className="font-display font-bold text-2xl text-splash-green">{PROGRAMS.find((p) => p.id === program)?.label} waitlist <span className="text-splash-green/50 text-lg">({(entries || []).filter((e) => e.position).length} active)</span></h1>
        <div className="flex items-center gap-3 text-sm">
          <label className="flex items-center gap-1 text-splash-green/70"><input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} data-testid="waitlist-show-closed" /> Show enrolled / released</label>
          <button type="button" onClick={load} className="inline-flex items-center gap-1 text-splash-teal font-semibold" data-testid="waitlist-refresh"><RefreshCw className="w-4 h-4" /> Refresh</button>
        </div>
      </div>
      <p className="text-xs text-splash-green/60 mb-4">Spots not claimed within 7 days are released automatically each day, and the next family is emailed.</p>
      {entries === null ? <Loader2 className="w-5 h-5 animate-spin text-splash-teal" /> : visible.length === 0 ? <div className="text-sm text-splash-green/60" data-testid="waitlist-empty">No one on this waitlist yet.</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead><tr className="text-[10px] uppercase tracking-widest text-splash-green/60">{['#', 'Parent', 'Child', 'Status', 'Notes', 'Actions'].map((h) => <th key={h} className="pb-2 pr-3">{h}</th>)}</tr></thead>
            <tbody>{visible.map((e) => <Row key={e.id} e={e} reload={load} />)}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function AdminWaitlists() {
  return <AdminGate title="Waitlist Manager"><Manager /></AdminGate>;
}
